import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import { generatePath } from 'quartz-routing/react-router';
import { AgentAccessPaths, ManagePaths, Paths } from '../constants/routes';
import { setWindowLocationHref } from 'hs-browser-apis/location';
export const getAiSettingsUrl = () => {
  const searchParams = new URLSearchParams();
  searchParams.set('eschref', window.location.pathname);
  return `${getFullUrl('app')}/ai-settings/${PortalIdParser.get()}?${searchParams.toString()}`;
};

// Agent Hub path prefix — used by in-app routes below and across the app.
export const getBaseUrl = () => '/agent-hub';
export const getRootUrl = () => `${getBaseUrl()}/${PortalIdParser.get()}`;

// Shared `agentInstanceId` / `executionId` query for both inbox surfaces.

const getInboxSearch = ({
  agentInstanceId,
  executionId,
  filterAgentInstanceIds
}) => {
  const searchParams = new URLSearchParams();
  // Selection param: drives which execution/agent is auto-selected on load.
  searchParams.set('agentInstanceId', String(agentInstanceId));
  if (executionId !== null && executionId !== void 0 && executionId.trim()) {
    searchParams.set('executionId', executionId);
  }
  if (filterAgentInstanceIds && filterAgentInstanceIds.length > 0) {
    searchParams.set('filterAgentInstanceIds', filterAgentInstanceIds.map(String).join(','));
  }
  const searchString = searchParams.toString();
  return searchString ? `?${searchString}` : '';
};
export const getManageAgentsRoute = () => Paths.MANAGE_AGENTS;
export const getManageAgentsUrl = () => `${getFullUrl('app')}${getRootUrl()}${Paths.MANAGE_AGENTS}`;
export const getManageIntroRoute = () => ManagePaths.INTRO;
export const getManageIntroUrl = () => `${getFullUrl('app')}${getRootUrl()}${ManagePaths.INTRO}`;
export const getManageAgentsListRoute = () => ManagePaths.AGENTS;
export const getManageAgentsListUrl = () => `${getFullUrl('app')}${getRootUrl()}${ManagePaths.AGENTS}`;
export const getManageAssistantsRoute = () => ManagePaths.ASSISTANTS;
export const getManageAssistantsUrl = () => `${getFullUrl('app')}${getRootUrl()}${ManagePaths.ASSISTANTS}`;
export const getManageAutomationsRoute = () => ManagePaths.AUTOMATIONS;
export const getManageAutomationsUrl = () => `${getFullUrl('app')}${getRootUrl()}${ManagePaths.AUTOMATIONS}`;
export const getManageNeedsAttentionRoute = () => ManagePaths.NEEDS_ATTENTION;
export const getManageNeedsAttentionUrl = () => `${getFullUrl('app')}${getRootUrl()}${ManagePaths.NEEDS_ATTENTION}`;

// Inbox inside Breeze Studio (`…/manage-agents/inbox`)
export const getInboxLandingUrl = () => `${getFullUrl('app')}${getRootUrl()}${ManagePaths.INBOX}`;
export const getInboxRoute = params => `${ManagePaths.INBOX}${getInboxSearch(params)}`;
export const getInboxUrl = params => `${getFullUrl('app')}${getRootUrl()}${getInboxRoute(params)}`;

// AgentInbox app (`/agent-inbox/...`)
export const getAgentInboxBaseUrl = () => '/agent-inbox';
export const getAgentInboxRootUrl = () => `${getAgentInboxBaseUrl()}/${PortalIdParser.get()}`;
export const getAgentInboxUrl = () => `${getFullUrl('app')}${getAgentInboxRootUrl()}`;
export const getAgentInboxSelectionUrl = params => `${getFullUrl('app')}${getAgentInboxRootUrl()}${getInboxSearch(params)}`;
export const getAgentDetailsRoute = agentInstanceId => generatePath(ManagePaths.AGENT_DETAILS, {
  agentInstanceId: String(agentInstanceId)
});
export const getAgentDetailsUrl = agentInstanceId => `${getFullUrl('app')}${getRootUrl()}${getAgentDetailsRoute(agentInstanceId)}`;
export const getAgentConnectionsRoute = agentInstanceId => generatePath(ManagePaths.AGENT_CONNECTIONS, {
  agentInstanceId: String(agentInstanceId)
});
export const getAgentConnectionsUrl = agentInstanceId => `${getFullUrl('app')}${getRootUrl()}${getAgentConnectionsRoute(agentInstanceId)}`;
export const getEditAgentAccessRoute = agentInstanceId => generatePath(AgentAccessPaths.EDIT_AGENT_ACCESS, {
  agentInstanceId: String(agentInstanceId)
});
export const getEditAgentAccessUrl = agentInstanceId => `${getFullUrl('app')}${getRootUrl()}${getEditAgentAccessRoute(agentInstanceId)}`;
export const getSettingsUrl = () => `/settings/${PortalIdParser.get()}/account-defaults/general`;
export const getCreditsSettingsUrl = () => `/account-and-billing/${PortalIdParser.get()}/usage/hubspot-credits`;
export const redirectToMainSettings = () => {
  setWindowLocationHref(`${getFullUrl('app')}${getSettingsUrl()}`);
};
export const getMarketplaceUrl = () => `${getFullUrl('app')}/marketplace/${PortalIdParser.get()}`;
export const getAgentMarketplaceUrl = referrer => {
  const searchParams = new URLSearchParams();
  searchParams.set('eco_PRODUCT_TYPE', 'AGENT');
  searchParams.set('eco_AGENT_TYPE', 'STUDIO');
  if (referrer) {
    searchParams.set('referrer', referrer);
  }
  return `${getMarketplaceUrl()}/explore?${searchParams.toString()}`;
};
export const getAssistantMarketplaceUrl = referrer => {
  const searchParams = new URLSearchParams();
  searchParams.set('eco_PRODUCT_TYPE', 'AGENT');
  searchParams.set('eco_AGENT_TYPE', 'ASSISTANT');
  if (referrer) {
    searchParams.set('referrer', referrer);
  }
  return `${getMarketplaceUrl()}/explore?${searchParams.toString()}`;
};
export const getMarketplaceListingUrl = (listingSlug, referrer = 'breeze') => {
  const searchParams = new URLSearchParams();
  searchParams.set('referrer', referrer);
  return `${getMarketplaceUrl()}/listing/${listingSlug}?${searchParams.toString()}`;
};
export const getConnectedAppsUrl = () => `${getFullUrl('app')}/connected-apps/${PortalIdParser.get()}/installed`;
export const getUserUrl = userId => `${getFullUrl('app')}/settings/${PortalIdParser.get()}/users/user/${userId}`;
export const getAgentUsersSettingsUrl = () => `${getFullUrl('app')}/settings/${PortalIdParser.get()}/users/agents`;
export const getUserPermissionsSettingsUrl = () => `${getFullUrl('app')}/settings/${PortalIdParser.get()}/users/permissions`;
const getWorkflowsBaseUrl = () => `${getFullUrl('app')}/workflows/${PortalIdParser.get()}`;
export const getCreateWorkflowUrl = ({
  agentInstanceId
}) => {
  const createUrl = `${getWorkflowsBaseUrl()}/create`;
  if (agentInstanceId) {
    const searchParams = new URLSearchParams();
    searchParams.set('agentInstanceId', String(agentInstanceId));
    return `${createUrl}?${searchParams.toString()}`;
  }
  return createUrl;
};
export const getEditWorkflowUrl = ({
  workflowId
}) => {
  return `${getWorkflowsBaseUrl()}/platform/flow/${workflowId}/edit`;
};
const getAutomationBaseUrl = () => `${getFullUrl('app')}/automation/${PortalIdParser.get()}`;
export const getAutomationFlowUrl = ({
  workflowId,
  actionId,
  referrer
}) => {
  let base = `${getAutomationBaseUrl()}/flow/${workflowId}`;
  if (actionId != null) {
    base += `/action/${actionId}`;
  }
  if (referrer) {
    const searchParams = new URLSearchParams();
    searchParams.set('referrer', referrer);
    return `${base}?${searchParams.toString()}`;
  }
  return base;
};
export const getModernEditorAgentUrl = ({
  agentInstanceId,
  referrer
}) => {
  const base = `${getAutomationBaseUrl()}/agent/${agentInstanceId}`;
  if (referrer) {
    const searchParams = new URLSearchParams();
    searchParams.set('referrer', referrer);
    return `${base}?${searchParams.toString()}`;
  }
  return base;
};
export const getCreateAgentUrl = () => `${getAutomationBaseUrl()}/agent/create`;